import { OptimisticPatch } from '../CollectionOptimisticPatch';
import { KeyedItem } from '../../model';

export function prepareItemsToMergeIntoPage<T>(
  patches: OptimisticPatch<T>[],
  {
    itemPredicate,
    currentItemsMap,
  }: {
    itemPredicate: (item: T) => boolean;
    currentItemsMap: Map<string, KeyedItem<T>>;
  },
) {
  const result = patches.reduce(
    (acc, optimisticPatch) => {
      const { patchedItemsEntries, actionType, keepPosition, selector } =
        optimisticPatch;

      if (actionType === 'deleteMany') {
        for (const [key] of patchedItemsEntries) {
          acc.all.delete(key);
          acc.deleted.set(key, true);
        }
        return acc;
      }

      if (actionType === 'deleteAll') {
        for (const [key, { item }] of Array.from(acc.all.entries())) {
          if (selector(item)) {
            acc.all.delete(key);
            acc.deleted.set(key, true);
          }
        }
        return acc;
      }

      if (
        actionType !== 'updateMany' &&
        actionType !== 'updateAll' &&
        actionType !== 'updateAllConst' &&
        actionType !== 'createMany'
      ) {
        return acc;
      }

      if (actionType === 'createMany') {
        acc.createdPatches.push(optimisticPatch);
      }

      for (const [key, item] of patchedItemsEntries) {
        if (currentItemsMap.has(key) || !itemPredicate(item)) {
          acc.all.delete(key);
          continue;
        }

        const prevPatch = acc.all.get(key);
        if (keepPosition && prevPatch) {
          prevPatch.item = item;
        } else {
          acc.all.set(key, { item, optimisticPatch });
        }
      }

      return acc;
    },
    {
      all: new Map<string, { item: T; optimisticPatch: OptimisticPatch<T> }>(),
      deleted: new Map<string, boolean>(),
      createdPatches: [] as OptimisticPatch<T>[],
    },
  );

  // saving the items order from "createMany" patches
  // if there were 2 "createMany" patches (added one by one): A (item1, item2), B (item3, item4)
  // the order of items should be [item3, item4, item1, item2, [the rest]]
  return {
    all: result.all,
    created: result.createdPatches.reverse().reduce((prev, optimisticPatch) => {
      const { patchedItemsEntries } = optimisticPatch;

      for (const [key, item] of patchedItemsEntries) {
        if (!result.deleted.get(key)) {
          prev.set(key, { item, optimisticPatch });
        }
      }
      return prev;
    }, new Map<string, { item: T; optimisticPatch: OptimisticPatch<T> }>()),
  };
}
